import { X } from 'lucide-react';
import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useBodyScrollLock } from '../lib/useBodyScrollLock';
import { usePdfSignedUrl, type PdfSignedUrl, type PdfSource } from '../lib/usePdfSignedUrl';

/**
 * In-page PDF preview overlay. Renders a centered card with the browser's
 * native PDF viewer inside an <iframe src={signedUrl}>. No new tab, no popup
 * blockers — Joey clicks View PDF, the PDF appears in front of him, he closes
 * it and returns to where he was.
 *
 * State contract: parent owns `source` (or null = closed) and an `onClose`
 * callback. One modal per page; multiple View buttons all dispatch into the
 * same state. A source is a daily PDF's id or any other document the API can
 * presign — see PdfSource.
 *
 * The signed-URL fetch lives in usePdfSignedUrl, shared with the side-by-side
 * compare viewer (PdfCompareModal) so neither can drift from the other.
 */
export function PdfPreviewModal({
  source,
  onClose,
}: {
  source: PdfSource | null;
  onClose: () => void;
}) {
  const { url, filename, error } = usePdfSignedUrl(source);

  // Open-ness is "a document is selected". Truthiness, not `!== null`, to match
  // the `if (!source) return null` guard below — otherwise an empty-string id
  // would render nothing while still holding the lock.
  useBodyScrollLock(!!source);

  // Esc to close.
  useEffect(() => {
    if (!source) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [source, onClose]);

  if (!source) return null;

  // Portaled for the same reason as PdfCompareModal: `main` is
  // `position: relative; z-index: 1`, a stacking context, so an overlay
  // rendered inside it can never paint above the side rail. This modal's
  // 1100px cap happened to centre clear of the rail, which hid the bug rather
  // than avoiding it — a wider viewport or a wider modal brings it straight
  // back. Purely a stacking fix; nothing about the viewer's behavior changes.
  return createPortal(
    <div
      className="pdf-modal-overlay"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="PDF preview"
    >
      <div className="pdf-modal" onClick={(e) => e.stopPropagation()}>
        <div className="pdf-modal-head">
          <div className="pdf-modal-title mono" title={filename}>
            {filename}
          </div>
          <button
            type="button"
            className="pdf-modal-close"
            onClick={onClose}
            aria-label="Close PDF preview"
          >
            <X size={14} strokeWidth={2} aria-hidden />
          </button>
        </div>
        <div className="pdf-modal-body">
          <PdfFrame url={url} filename={filename} error={error} />
        </div>
      </div>
    </div>,
    document.body,
  );
}

/**
 * The three states a PDF pane can be in. Shared with PdfCompareModal, which
 * renders two of them — each pane owning its own load and its own failure, so
 * one unreachable document doesn't blank the other.
 */
export function PdfFrame({ url, filename, error }: PdfSignedUrl) {
  if (error) {
    return (
      <div className="pdf-modal-status" style={{ color: 'var(--danger)' }}>
        {error}
      </div>
    );
  }
  if (!url) return <div className="pdf-modal-status muted">Loading PDF…</div>;
  return <iframe src={url} title={filename} className="pdf-modal-iframe" />;
}
